import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { UploadCsvStatusEnum, WIDTH_MODAL_COMPONENTS } from '../../../../../constants/addressBook';
import { formatBytesToKB } from '../../../../../utils/helpers';

interface IProps {
  file?: File | null;
  status: UploadCsvStatusEnum;
  onDelete: () => void;
}

const FilePreview: React.FC<IProps> = ({ file, status, onDelete }) => {
  const sizeLabel = useMemo(() => (file?.size ? `${formatBytesToKB(file.size)}KB` : 'File too large'), [file]);

  const isComplete = useMemo(() => status === UploadCsvStatusEnum.Complete, [status]);
  const isError = useMemo(() => status === UploadCsvStatusEnum.ErrorLargeFile, [status]);

  const textStyle = useMemo(() => (isError ? { color: 'error.main' } : { color: 'text.secondary' }), [isError]);

  const statusLabel = useMemo(() => {
    switch (true) {
      case isComplete:
        return 'Complete';
      case isError:
        return 'Failed';
      default:
        return 'Loading';
    }
  }, [isComplete, isError]);

  const nameLabel = useMemo(() => (file ? file.name : 'Upload failed.'), [file]);

  return (
    <Box
      sx={{
        maxWidth: WIDTH_MODAL_COMPONENTS,
        width: '100%',
        mx: 'auto',
        borderRadius: 0.5,
        padding: 2,
        boxShadow: (theme) => theme.shadows[1],
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="column" justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 16, color: isError ? 'error.main' : 'text.primary' }}>{nameLabel}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={textStyle}>
              {sizeLabel}
            </Typography>
            <Typography variant="body2" sx={textStyle}>
              •
            </Typography>
            <Typography variant="body2" sx={textStyle}>
              {statusLabel}
            </Typography>
          </Stack>
          <LinearProgress
            sx={{ width: 200, opacity: isComplete || isError ? 0.9 : 1 }}
            variant={isComplete || isError ? 'determinate' : 'indeterminate'}
            value={isComplete ? 100 : 0}
            color={isError ? 'error' : 'primary'}
          />
        </Stack>
        <IconButton
          onClick={onDelete}
          size="small"
          sx={{
            ml: 0.5,
            color: (theme) => theme.palette.action.active,
            '&:hover': {
              bgcolor: (theme) => theme.palette.action.hover,
            },
          }}
          data-action="file_preview_delete_btn"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default FilePreview;
